import { RELATED_AS_LABELS } from "../../../api/stakeholder_relationships";
import { Employee, LoanAccount, PrimaryParty } from "../../../api/creditAccounts";

/**
 * Pure validators for the Party-Involved sections. Reused by inline field
 * errors, the "Add …" enable state, the Next-gate selector, and submit.
 */

export type FieldErrors = Record<string, string>;

// --- Loan accounts ---
export const getAccountRowErrors = (
  row: LoanAccount,
  all: LoanAccount[]
): FieldErrors => {
  const errors: FieldErrors = {};
  if (!row.extAccountNumber.trim()) {
    errors.extAccountNumber = "Account number is required.";
  } else if (
    all.filter((a) => a.extAccountNumber.trim() === row.extAccountNumber.trim())
      .length > 1
  ) {
    errors.extAccountNumber = "Account number must be unique.";
  }
  if (!row.product) errors.product = "Product is required.";
  if (!row.creditFacility) errors.creditFacility = "Credit facility is required.";
  if (!row.accountName.trim()) errors.accountName = "Account name is required.";
  return errors;
};

export const isAccountRowValid = (row: LoanAccount, all: LoanAccount[]) =>
  Object.keys(getAccountRowErrors(row, all)).length === 0;

export const isAccountsSectionValid = (all: LoanAccount[]) =>
  all.length > 0 && all.every((r) => isAccountRowValid(r, all));

// --- Primary parties (party-centric) ---
export const getPartyErrors = (
  party: PrimaryParty,
  all: PrimaryParty[]
): { name?: string; relations: Record<string, string>; account_numbers: Record<string, string> } => {
  const relations: Record<string, string> = {};
  const account_numbers: Record<string, string> = {};
  let name: string | undefined;

  const normalizedPartyName = party.name.replace(/\s+/g, "").toLowerCase();

  if (!party.name.trim()) {
    name = "Party name is required.";
  } else if (
    all.filter(
      (p) => p.name.replace(/\s+/g, "").toLowerCase() === normalizedPartyName
    ).length > 1
  ) {
    name = "Party name must be unique.";
  }

  party.account_relationships.forEach((r) => {
    if (!r.related_as) relations[r.id] = "Party type is required.";
  });

  party.account_relationships.forEach((r) => {
    if (!r.account_numbers || r.account_numbers.length === 0) {
      account_numbers[r.id] = "Atleast 1 account number is required.";
    }
  });

  party.account_relationships.forEach((r) => {
    if (!r.related_as) return;
    const isDuplicate = party.account_relationships.some(
      (other) =>
        other.id !== r.id &&
        other.related_as === r.related_as &&
        other.account_numbers.some((num) => r.account_numbers.includes(num))
    );
    if (isDuplicate) {
      const label = RELATED_AS_LABELS[r.related_as] || r.related_as;
      account_numbers[r.id] = `Account already added for ${label}`;
    }
  });

  return { name, relations, account_numbers };
};

export const isPartyValid = (party: PrimaryParty, all: PrimaryParty[]) => {
  const { name, relations, account_numbers } = getPartyErrors(party, all);
  return (
    !name &&
    Object.keys(relations).length === 0 &&
    Object.keys(account_numbers).length === 0 &&
    party.account_relationships.length > 0
  );
};

export const isPartiesSectionValid = (all: PrimaryParty[]) =>
  all.every((p) => isPartyValid(p, all));

// --- Employees (derived from employee-type party mappings) ---
export const getEmployeeRowErrors = (row: Employee): FieldErrors => {
  const errors: FieldErrors = {};
  if (!row.types) errors.type = "Type is required.";
  if (!row.name.trim()) errors.name = "Name is required.";
  if (!row.employeeId || !row.employeeId.trim()) {
    errors.employeeId = "Employee ID is required.";
  }
  return errors;
};

export const isEmployeeRowValid = (row: Employee) =>
  Object.keys(getEmployeeRowErrors(row)).length === 0;

// No manual employees — every derived employee must be complete (Employee ID
// is mandatory), so an employee-type party without an ID blocks the step.
export const isEmployeesSectionValid = (all: Employee[]) =>
  all.every(isEmployeeRowValid);

// --- Whole step ---
export const isPartyInvolvedSectionsValid = (
  accounts: LoanAccount[],
  parties: PrimaryParty[],
  employees: Employee[]
) =>
  isAccountsSectionValid(accounts) &&
  isPartiesSectionValid(parties) &&
  isEmployeesSectionValid(employees);